import { useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { Btn, Chip, Section, inputCls } from "@/components/account/Shell";
import api, { formatApiError } from "@/lib/api";

const Picker = ({ value, onPick, testid }) => (
    <span className="inline-flex items-center gap-px" data-testid={testid}>
        {[1, 2, 3, 4, 5].map((i) => (
            <button key={i} type="button" onClick={() => onPick(i)} aria-label={`${i} star`}
                data-testid={`${testid}-${i}`}>
                <Star size={17} strokeWidth={2}
                    className={i <= value ? "fill-[#b54708] text-[#b54708]" : "text-[#d0d0d0] hover:text-[#4a4a4a]"} />
            </button>
        ))}
    </span>
);

function One({ job, onDone }) {
    const [rating, setRating] = useState(5);
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [busy, setBusy] = useState(false);

    const post = async () => {
        setBusy(true);
        try {
            await api.post("/reviews", { order_id: job.order_id, product_slug: job.product_slug, rating, title, body });
            toast.success("Thanks — your review is live on their store");
            onDone();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div className="border-t border-[#d0d0d0] px-2.5 py-2 first:border-t-0"
            data-testid={`write-review-${job.product_slug}`}>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-sm font-bold text-[#1a1a1a]">{job.product_name}</span>
                <Chip tone="neutral">{job.store_name || job.store_slug}</Chip>
                <span className="text-[12px] text-[#4a4a4a]">order {job.order_number}</span>
                <span className="ml-auto"><Picker value={rating} onPick={setRating} testid={`review-rate-${job.product_slug}`} /></span>
            </div>
            <div className="mt-1.5 flex flex-wrap items-start gap-1.5">
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="One line — the headline"
                    data-testid={`review-title-${job.product_slug}`} className={`${inputCls} min-w-[12rem] max-w-[20rem] flex-1`} />
                <input value={body} onChange={(e) => setBody(e.target.value)} placeholder="How did the job go?"
                    data-testid={`review-body-${job.product_slug}`} className={`${inputCls} min-w-[16rem] flex-[2]`} />
                <Btn tone="solid" disabled={busy || !body.trim()} testid={`review-post-${job.product_slug}`} onClick={post}>
                    Post review
                </Btn>
            </div>
        </div>
    );
}

/** The buyer's side of reviews: rate any job a shop has finished for you. */
export default function WriteReview({ jobs, written, onDone }) {
    if (!jobs?.length && !written?.length) return null;
    return (
        <div className="space-y-2.5">
            {!!jobs?.length && (
                <Section title="Rate a finished job" pad={false} testid="write-review-list"
                    note={`${jobs.length} waiting · a review only counts on a job that's done`}>
                    {jobs.map((j) => <One key={`${j.order_id}-${j.product_slug}`} job={j} onDone={onDone} />)}
                </Section>
            )}
            {!!written?.length && (
                <Section title="Reviews you wrote" pad={false} testid="my-reviews-list">
                    {written.map((r) => (
                        <div key={r.id} className="border-t border-[#d0d0d0] px-2.5 py-1.5 first:border-t-0"
                            data-testid={`my-review-${r.id}`}>
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <Picker value={r.rating} onPick={() => { }} testid={`my-review-stars-${r.id}`} />
                                <span className="text-sm font-semibold text-[#1a1a1a]">{r.title || `${r.rating} out of 5`}</span>
                                <span className="text-[12px] text-[#4a4a4a]">{r.store_name} · {r.product_name}</span>
                            </div>
                            <p className="mt-0.5 text-[13px] text-[#1a1a1a]">{r.body}</p>
                            {r.reply && (
                                <p className="mt-1 border-l-2 border-[#1a1a1a] bg-[#fafafa] px-2 py-1 text-[12px] text-[#1a1a1a]">
                                    <span className="font-semibold">{r.reply.by} replied: </span>{r.reply.body}
                                </p>
                            )}
                        </div>
                    ))}
                </Section>
            )}
        </div>
    );
}
